import { cn } from '@/utils/cn';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  text?: string;
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-8 h-8',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
};

const borderSizes = {
  sm: 'border-2',
  md: 'border-3',
  lg: 'border-4',
  xl: 'border-4',
};

/**
 * LoadingSpinner Component
 * A reusable loading spinner with customizable size
 */
export function LoadingSpinner({ size = 'md', className, text }: LoadingSpinnerProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
      <div
        className={cn(
          sizeClasses[size],
          borderSizes[size],
          'border-primary-200 border-t-primary-600 dark:border-secondary-700 dark:border-t-primary-500 rounded-full animate-spin'
        )}
        role="status"
        aria-label={text ?? 'Loading'}
      />
      {text && (
        <p className="text-sm text-secondary-600 dark:text-secondary-400">
          {text}
        </p>
      )}
    </div>
  );
}

/**
 * FullPageLoader Component
 * A full-page loading screen with spinner
 */
export function FullPageLoader({ text = 'Loading...' }: { text?: string }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-white dark:bg-secondary-900">
      <LoadingSpinner size="lg" text={text} />
    </div>
  );
}

/**
 * PageLoader Component
 * A page-level loading screen (within layout)
 */
export function PageLoader({ text = 'Loading...' }: { text?: string }) {
  return (
    <div className="flex items-center justify-center min-h-[400px] p-6">
      <LoadingSpinner size="lg" text={text} />
    </div>
  );
}

/**
 * InlineLoader Component
 * A small inline loading indicator
 */
export function InlineLoader({ text }: { text?: string }) {
  return (
    <div className="flex items-center gap-2">
      <LoadingSpinner size="sm" />
      {text && (
        <span className="text-sm text-secondary-600 dark:text-secondary-400">
          {text}
        </span>
      )}
    </div>
  );
}

/**
 * ButtonLoader Component
 * A loading spinner for buttons
 */
export function ButtonLoader() {
  return (
    <svg
      className="w-4 h-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
